import { addDays, format, startOfWeek } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useToday } from "./useToday";

export interface TwoWeekWindow {
	/** ISO date string (yyyy-MM-dd) — Monday of the visible window */
	from: string;
	/** ISO date string (yyyy-MM-dd) — Sunday at end of window (day +13 from `from`) */
	to: string;
	windowStart: Date;
	windowEnd: Date;
	todayIso: string;
	prev: () => void;
	next: () => void;
	goToToday: () => void;
}

/**
 * 14-day rolling window starting on the Monday of the anchor's week.
 *
 * **Default behavior:** when called as `useTwoWeekWindow()` (no anchor), the
 * window is anchored on today (C-02). `prev`/`next` page by exactly 14 days
 * (CAL-03). Hardcoded `weekStartsOn: 1` (Monday) for all 5 locales (C-02).
 *
 * **Phase 38 D-11 anchor extension:** an optional `anchor: Date` parameter
 * lets the caller re-anchor the window. The window re-anchors whenever
 * `anchor` transitions from undefined → a defined Date (or to a different
 * Date). This is required for the deep-link consumer pattern: when a
 * notification deep-links into /track?day=YYYY-MM-DD while TrackPage is
 * already mounted, React Router treats it as a same-route param change and
 * does NOT remount the page. Without the re-anchor effect, the hook would
 * keep its stale `windowStart` from the user's first landing on /track and
 * the calendar would never page to the targeted day.
 *
 * Re-anchor does NOT fight user paging because TrackPage clears `?day=` via
 * `setSearchParams` immediately after consuming it. The anchor goes back to
 * `undefined` on the next render, and the effect's `if (anchor)` guard
 * prevents re-firing. Subsequent prev/next/goToToday calls update windowStart
 * directly without anchor involvement.
 *
 * Callers passing a derived/memoized anchor must memoize stably (TrackPage
 * does this via useMemo over the parsed dayParam) — otherwise every render
 * with the same logical date would create a new Date object and re-anchor
 * unnecessarily.
 */
export function useTwoWeekWindow(anchor?: Date): TwoWeekWindow {
	const { today, todayIso } = useToday();
	const initialAnchor = anchor ?? today;
	const initialMonday = startOfWeek(initialAnchor, { weekStartsOn: 1 });
	const [windowStart, setWindowStart] = useState<Date>(initialMonday);

	// Re-anchor when `anchor` transitions to a defined Date (e.g., a deep-link
	// arrives while TrackPage is already mounted). No-op when `anchor` is
	// undefined so user-initiated prev/next paging is preserved.
	useEffect(() => {
		if (!anchor) return;
		setWindowStart(startOfWeek(anchor, { weekStartsOn: 1 }));
	}, [anchor]);

	const windowEnd = addDays(windowStart, 13);

	const prev = useCallback(() => {
		setWindowStart((current) => addDays(current, -14));
	}, []);

	const next = useCallback(() => {
		setWindowStart((current) => addDays(current, 14));
	}, []);

	const goToToday = useCallback(() => {
		setWindowStart(startOfWeek(today, { weekStartsOn: 1 }));
	}, [today]);

	return {
		from: format(windowStart, "yyyy-MM-dd"),
		to: format(windowEnd, "yyyy-MM-dd"),
		windowStart,
		windowEnd,
		todayIso,
		prev,
		next,
		goToToday,
	};
}
